export const VOL_BOUGHT_VALIDATION_TOLERANCE = 0.05;

const INPUT_CLASS_GROUPS = {
  match: [
    "border-success-300",
    "bg-success-50",
    "focus:border-success-700",
    "focus:ring-success-700/25",
  ],
  close: [
    "border-warning-300",
    "bg-warning-50",
    "focus:border-warning-500",
    "focus:ring-warning-500/25",
  ],
  mismatch: [
    "border-red-300",
    "bg-red-50",
    "focus:border-red-500",
    "focus:ring-red-500/25",
  ],
  unknown: [
    "border-warning-300",
    "bg-warning-50",
    "focus:border-warning-500",
    "focus:ring-warning-500/25",
  ],
  neutral: [
    "border-warning-200",
    "bg-warning-50",
    "focus:border-brand-600",
    "focus:ring-brand-600/25",
  ],
};

const CELL_CLASS_GROUPS = {
  match: [ "bg-success-50" ],
  close: [ "bg-warning-50" ],
  mismatch: [ "bg-red-50" ],
  unknown: [ "bg-warning-50" ],
  neutral: [ "bg-warning-50" ],
};

const READONLY_CLASS_GROUPS = {
  match: [ "inline-flex", "min-h-10", "w-full", "items-center", "rounded-lg", "border", "px-3", "text-sm", "tabular-nums", "border-success-300", "bg-success-50", "text-success-900" ],
  close: [ "inline-flex", "min-h-10", "w-full", "items-center", "rounded-lg", "border", "px-3", "text-sm", "tabular-nums", "border-warning-300", "bg-warning-50", "text-warning-900" ],
  mismatch: [ "inline-flex", "min-h-10", "w-full", "items-center", "rounded-lg", "border", "px-3", "text-sm", "tabular-nums", "border-red-300", "bg-red-50", "text-red-900" ],
  unknown: [ "inline-flex", "min-h-10", "w-full", "items-center", "rounded-lg", "border", "px-3", "text-sm", "tabular-nums", "border-warning-300", "bg-warning-50", "text-warning-900" ],
  neutral: [ "text-sm", "tabular-nums", "text-gray-modern-900" ],
};

const ALL_INPUT_CLASSES = [ ...new Set(Object.values(INPUT_CLASS_GROUPS).flat()) ];
const ALL_CELL_CLASSES = [ ...new Set(Object.values(CELL_CLASS_GROUPS).flat()) ];
const ALL_READONLY_CLASSES = [ ...new Set(Object.values(READONLY_CLASS_GROUPS).flat()) ];

export function parseVolBoughtValue(raw) {
  const normalized = String(raw ?? "").replace(/,/g, "").trim();
  if (normalized === "") return 0;

  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? value : 0;
}

export function volBoughtValidationStatus(volBought, volDeclared) {
  const bought = parseVolBoughtValue(volBought);
  const declared = Number(volDeclared) || 0;

  if (declared <= 0) return bought <= 0 ? "neutral" : "unknown";
  if (Number(bought.toFixed(1)) === Number(declared.toFixed(1))) return "match";

  const ratio = Math.abs(bought - declared) / declared;
  if (ratio <= VOL_BOUGHT_VALIDATION_TOLERANCE) return "close";

  return "mismatch";
}

function applyClassGroup(element, allClasses, nextClasses) {
  element.classList.remove(...allClasses);
  element.classList.add(...nextClasses);
}

export function applyVolBoughtValidationInput(input) {
  if (!input) return;

  const declared = Number(input.dataset.volDeclared) || 0;
  const status = volBoughtValidationStatus(input.value, declared);
  const cell = input.closest("[data-scenario-modeling-intermediaries-target='volBoughtCell']");

  applyClassGroup(input, ALL_INPUT_CLASSES, INPUT_CLASS_GROUPS[status]);
  input.dataset.volBoughtValidationStatus = status;
  input.setAttribute("aria-invalid", status === "mismatch" ? "true" : "false");

  if (cell) {
    applyClassGroup(cell, ALL_CELL_CLASSES, CELL_CLASS_GROUPS[status]);
    cell.dataset.volBoughtValidationStatus = status;
  }
}

export function applyVolBoughtValidationReadonly(element) {
  if (!element) return;

  const declared = Number(element.dataset.volDeclared) || 0;
  const bought = element.dataset.volBought ?? element.textContent;
  const status = volBoughtValidationStatus(bought, declared);

  applyClassGroup(element, ALL_READONLY_CLASSES, READONLY_CLASS_GROUPS[status]);
  element.dataset.volBoughtValidationStatus = status;
}

export function refreshVolBoughtValidation(root = document) {
  root.querySelectorAll("[data-scenario-modeling-intermediaries-target='volBoughtInput']").forEach((input) => {
    applyVolBoughtValidationInput(input);
  });

  root.querySelectorAll("[data-scenario-modeling-intermediaries-target='volBoughtReadonly']").forEach((element) => {
    applyVolBoughtValidationReadonly(element);
  });
}

export function syncVolDeclaredDataset(root = document) {
  root.querySelectorAll("[data-scenario-modeling-intermediaries-target='volDeclaredCell']").forEach((cell) => {
    const userId = cell.dataset.userId;
    if (!userId) return;

    const amount = cell.querySelector("[data-role='amount']");
    const declared = Number((amount?.textContent || cell.textContent).replace(/,/g, "")) || 0;
    const input = root.querySelector(
      `[data-scenario-modeling-intermediaries-target='volBoughtInput'][data-user-id='${userId}']`,
    );

    if (input) input.dataset.volDeclared = String(declared);
  });
}
